import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { LinkButton } from '../../components/ui/Button';
import { brand } from '../../config/brand';
import { platform, type Explanation } from '../home/api';
import { useSession } from '../../stores/session';

/**
 * What hosting a room here actually involves, for somebody deciding.
 *
 * ## Why a page and not a paragraph on the front one
 *
 * The landing page has one job: explain a platform to somebody who might listen
 * or upload. Hosting is a different decision made by a much smaller number of
 * people, and it is the decision the whole platform depends on - a site of
 * submissions with nobody to play them is a submission box. Somebody weighing
 * it up wants the console's contents, not a slogan, and putting that on the
 * front page would bury the thing everybody else came for.
 *
 * ## Signed out only, and the header says so
 *
 * A host who is already here has the console; a listener who is signed in has
 * the account menu and the application form. The one reader this is for is the
 * stranger, so the nav item is theirs alone - see `AppShell`. The page itself
 * stays reachable by URL for anybody, because a host sending it to a friend
 * should not have their link redirect.
 *
 * ## Every claim is checked against the console
 *
 * The sections below were written by reading `ConsolePage`, `QueueTable`,
 * `RoomSettingsDialog`, `OverlayDialog`, `ReviewPanel` and `SessionPanel`, and
 * they describe what those do rather than what would sound good. The two places
 * that could drift are numbers and switches, so neither is typed here: the
 * weights and the revenue split come from `/explain`, and the optional
 * capabilities - the browser studio, tips, paid boosts - are gated on the flags
 * the same endpoint reports. A stranger reading about a studio that is switched
 * off finds out after making an account, which is the worst moment to find out.
 */
export function ForHostsPage() {
  const user = useSession((state) => state.user);

  const { data: explain } = useQuery({
    queryKey: ['explain'],
    queryFn: () => platform.explain(),
    staleTime: 10 * 60_000,
  });

  const studio = explain?.features.studio ?? false;
  const tips = explain?.features.tips ?? false;
  const paidBoosts = explain?.features.boosts_paid ?? false;

  return (
    <div className="flex flex-col" data-for-hosts>
      <Hero signedIn={user !== null} />

      <div className="mx-auto flex w-full max-w-6xl flex-col gap-20 px-4 py-16 sm:gap-24 sm:px-6 sm:py-20">
        <OnAir studio={studio} />
        <Queue explain={explain} paidBoosts={paidBoosts} />
        <Room />
        <Reach />
        <Feedback />
        <Money explain={explain} tips={tips} paidBoosts={paidBoosts} />
        <Ending signedIn={user !== null} />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function Hero({ signedIn }: { signedIn: boolean }) {
  return (
    <section className="relative overflow-hidden border-b border-line">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-70"
        style={{
          background:
            'radial-gradient(1200px 480px at 12% -10%, color-mix(in oklab, var(--color-accent) 22%, transparent), transparent 70%),' +
            'radial-gradient(900px 420px at 88% 0%, color-mix(in oklab, var(--color-hot) 16%, transparent), transparent 70%)',
        }}
      />

      <div className="relative mx-auto flex w-full max-w-6xl flex-col gap-10 px-4 py-16 sm:px-6 sm:py-20 lg:flex-row lg:items-center lg:gap-14">
        <div className="flex max-w-xl flex-col gap-5">
          <span className="meta-caps text-faint">For hosts</span>

          <h1 className="text-pretty text-4xl font-bold leading-[1.05] tracking-tight text-ink sm:text-5xl">
            A room of your own, and a console to run it from.
          </h1>

          <p className="text-pretty text-[16px] leading-relaxed text-soft">
            Play what you want, take submissions from anybody, and put your taste on air. The
            console is one screen: the deck on the left, the queue in the middle, the room down the
            side. Everything a set needs is on it, from who may speak to what happens when the queue
            runs dry.
          </p>

          <div className="flex flex-wrap gap-3 pt-1">
            <LinkButton
              to={signedIn ? '/host' : '/register'}
              className="px-7 py-3.5 text-base font-bold"
            >
              {signedIn ? 'Apply to host' : 'Create an account'}
            </LinkButton>
            <LinkButton to="/discover" variant="secondary" className="px-7 py-3.5 text-base">
              Listen first
            </LinkButton>
          </div>

          <p className="font-mono text-[11px] leading-relaxed text-faint">
            Hosting is by application. It is somebody's evening on air, so a person reads every
            one.
          </p>
        </div>

        {/*
          The console itself, because the page is about the console. A capture
          of the real build rather than a mock, for the same reason the front
          page shows an actual broadcast: a rendered mock is the one image on a
          site that can go stale without anybody noticing.
        */}
        <figure className="min-w-0 flex-1">
          <div className="overflow-hidden rounded-[var(--radius-card)] border border-line bg-void shadow-[0_24px_60px_-40px_rgba(0,0,0,0.8)]">
            <img
              src="/screens/console.webp"
              alt="The host console: the studio preview on the left, the submission queue in the middle, and session stats and room chat down the right."
              width={1600}
              height={860}
              decoding="async"
              className="block h-auto w-full"
            />
          </div>
        </figure>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */

function OnAir({ studio }: { studio: boolean }) {
  return (
    <Section
      eyebrow="Going on air"
      title="Three ways to broadcast, and you can change your mind mid-set."
      lead="A broadcast has a source, and the console asks which. Switching between them does not drop a listener. The room keeps its playhead, its chat and its queue."
    >
      <Cards>
        <Card title="Play your own tracks">
          The room plays from here: your records, what people submit, or anything you pull in from
          the rest of the site. No camera, no encoder, nothing to install. Press go and the deck
          starts.
        </Card>

        {/*
          Only when the flag says so. A page that described the browser studio
          on an installation where it is switched off would be selling the one
          feature a new host cannot reach.
        */}
        {studio && (
          <Card title="Broadcast from the browser">
            Camera, artwork and the now-playing card composited on one canvas, mixed with your
            deck's audio and published straight from the tab. Set the scene up and preview it
            before anything goes live.
          </Card>
        )}

        <Card title="Point an encoder at it">
          A stream key for OBS, Streamlabs or anything that speaks RTMP, and the room carries what
          you send. Listeners watch it in the room the same way they watch anything else.
        </Card>

        <Card title="Say where else it is">
          Streaming to Twitch or YouTube as well? Name the platform and the room points people at
          it, resolved from your own profile link, so a badge can never outlive the address it
          points at.
        </Card>
      </Cards>
    </Section>
  );
}

/* ------------------------------------------------------------------ */

function Queue({ explain, paidBoosts }: { explain?: Explanation; paidBoosts: boolean }) {
  return (
    <Section
      eyebrow="The queue"
      title="Submissions arrive in an order you can see, and change."
      lead="Anybody can send you a track. What decides the running order is a weight, and the console prints the same legend the sorter uses, so the queue is never a black box, to you or to the person waiting in it."
    >
      {/*
        Straight from `/explain`, which reads `Weighting::legend()` - the same
        source the console's own legend prints. A page that typed these numbers
        would be wrong the day one changed, and this is the page somebody
        decides on.
      */}
      {explain && (
        <div className="flex flex-col gap-2 rounded-[var(--radius-card)] border border-line bg-surface p-5">
          <span className="meta-caps text-ghost">How a queue is ordered</span>

          <dl className="flex flex-col gap-1.5">
            {explain.weighting.tiers.map((tier) => (
              <div
                key={tier.tier}
                className="flex items-center justify-between gap-4 font-mono text-[12px]"
              >
                <dt className="truncate text-soft">{tier.label}</dt>
                <dd className="shrink-0 text-ink">
                  {tier.pinned ? 'pinned above everything' : `×${tier.weight}`}
                </dd>
              </div>
            ))}

            {explain.weighting.bonuses.map((bonus) => (
              <div
                key={bonus.label}
                className="flex items-center justify-between gap-4 font-mono text-[12px]"
              >
                <dt className="truncate text-faint">{bonus.label}</dt>
                <dd className="shrink-0 text-faint">+{bonus.weight}</dd>
              </div>
            ))}
          </dl>

          <p className="font-mono text-[11px] text-faint">
            ties: {explain.weighting.tie_break}
          </p>
        </div>
      )}

      <Cards>
        <Card title="Move anything, any time">
          Bump a track to the top, pin it as a host pick above every weight, drop it, or roll for a
          random one when you cannot decide. Turn the weighting off entirely and run the order
          yourself.
        </Card>

        <Card title="Keep it worth submitting to">
          Cap how many free submissions one person can hold at a time, and guarantee that every
          Nth track played is a free one
          {paidBoosts ? ', so a paid queue never turns into a toll gate' : ''}. Both are off until
          you turn them on.
        </Card>

        <Card title="Never go quiet">
          If the queue runs low the room can top itself up: from your own tracks, from one of your
          playlists in order, or from across the platform, favoring artists nobody has heard yet.
          You set the threshold.
        </Card>

        <Card title="Bring your own">
          Search everything on the site from the console and drop anything into the queue, or push
          a playlist in to warm the room up. Reorder, remove and pin all still apply.
        </Card>

        <Card title="Decide who gets in">
          Open submissions to everyone, to your followers, or close them, from the header while you
          are on air, because that is when a room needs it. Refuse explicit tracks at the door and
          the submitter is told why.
        </Card>

        <Card title="One link to hand out">
          The running order lives at an address you can paste into a stream chat. It opens on a
          phone and answers the only two questions anybody has: what is coming, and how long until
          mine.
        </Card>
      </Cards>
    </Section>
  );
}

/* ------------------------------------------------------------------ */

function Room() {
  return (
    <Section
      eyebrow="The room"
      title="It is a place, not a player."
      lead="However you broadcast, listeners land on your profile and the room is the top of it, with the people who are actually in it drawn standing in front of you."
    >
      <Cards>
        <Card title="A venue, or just the music">
          A drawn stage with you behind the decks, the crowd in front and the artwork on the screen
          behind. Or the plain deck, if that is the room you want. Your choice, per room.
        </Card>

        <Card title="Chat, on your terms">
          Everyone, followers only, or off. You can always speak, and anything can be taken down.
          The transcript stays readable either way.
        </Card>

        <Card title="An applause meter">
          Listeners tap to cheer and the number climbs for everybody at once, with fireworks on
          every thousand. The console tells you the loudest minute of the night and what was
          playing in it.
        </Card>

        <Card title="Listed, or not">
          Rooms appear on the live rail while you are on air. Turn that off and yours is unlisted:
          open to anyone with the link, and off the front of the site.
        </Card>
      </Cards>
    </Section>
  );
}

/* ------------------------------------------------------------------ */

function Reach() {
  return (
    <Section
      eyebrow="Getting an audience in"
      title="The people who follow you find out, and your stream can show the room."
    >
      <Cards>
        <Card title="Followers are told">
          Going live notifies the people following you, once. A reconnect two minutes later is
          silent, which is the difference between useful and the reason somebody mutes you.
        </Card>

        <Card title="On the rail, everywhere">
          A live room shows up in the site header, on the dashboard and on the front page, for
          everybody, signed in or not. Listening needs no account.
        </Card>

        <Card title="Stream graphics">
          Browser sources for OBS, Streamlabs or TikTok Studio: what is playing, what is coming up,
          and what has already played, drawn on a transparent background in a few styles and
          updating themselves.
        </Card>

        <Card title="Anybody can listen along">
          No login wall on a broadcast. Somebody arriving from your stream chat can hear it
          immediately, and make an account when they want to submit something.
        </Card>
      </Cards>
    </Section>
  );
}

/* ------------------------------------------------------------------ */

function Feedback() {
  return (
    <Section
      eyebrow="What you get back"
      title="A set leaves something behind."
      lead="The console keeps the parts of an evening worth keeping, and gives you somewhere to say what you thought of a track while it is still playing."
    >
      <Cards>
        <Card title="Tell the artist, or tell yourself">
          A note on whatever is on the deck. Send it and they are notified; save it privately and
          it is a crate note nobody else ever sees. The private half is what makes the sent half
          worth reading.
        </Card>

        <Card title="What happened, as it happens">
          Listeners now and at peak, tracks and artists played, how many are waiting, how many were
          boosted, and how many you skipped early, which is the most useful number a host has about
          their own taste.
        </Card>

        <Card title="The shape of the night">
          A listener curve for the last ninety minutes, so you can tell the top of a climb from the
          bottom of a fall, next to the minute the room went off hardest.
        </Card>

        <Card title="What you played">
          The history of the set, in the console, for when somebody in the chat asks what was
          playing two songs ago, and for a second chance to say something about it.
        </Card>
      </Cards>
    </Section>
  );
}

/* ------------------------------------------------------------------ */

function Money({
  explain,
  tips,
  paidBoosts,
}: {
  explain?: Explanation;
  tips: boolean;
  paidBoosts: boolean;
}) {
  // Nothing is switched on, so there is nothing honest to say here yet.
  if (!tips && !paidBoosts) return null;

  const share = explain === undefined ? null : Math.round(explain.revenue.host_share_bp / 100);

  return (
    <Section
      eyebrow="Money"
      title={`Rooms can take money, and ${share ?? 80}% of it is yours.`}
      lead="Payments run through Stripe under your own account, which you connect before going on air. The share is of the net, after the processor's cut. A page that said otherwise would be telling half a truth about somebody's money."
    >
      <Cards>
        {tips && (
          <Card title="Tips">
            Listeners can tip you during a broadcast. It lands on the console the moment it
            arrives, because a host mid-set has no hands free to go looking.
          </Card>
        )}

        {paidBoosts && (
          <Card title="Paid boosts">
            Somebody submitting can pay to move up your queue.
            {explain
              ? ` A basic boost is ${money(explain.boosts.basic_cents)} and priority is ${money(
                  explain.boosts.priority_cents,
                )} by default, and you can price your own room.`
              : ''}{' '}
            Earned boosts, which nobody can buy, still outrank both.
          </Card>
        )}

        <Card title="Nothing is taken from artists">
          A boost is a listener moving a track up your queue. Artists are not charged to be played
          and are not paid from it. What they earn from championing other people is credit, not
          money.
        </Card>
      </Cards>
    </Section>
  );
}

/** `$5` from cents, with the pennies only when there are any. */
function money(cents: number): string {
  return cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`;
}

/* ------------------------------------------------------------------ */

function Ending({ signedIn }: { signedIn: boolean }) {
  return (
    <section className="flex flex-wrap items-center justify-between gap-8 rounded-[var(--radius-card)] border border-line-strong bg-surface p-8 sm:p-12">
      <div className="flex max-w-xl flex-col gap-3">
        <h2 className="text-pretty text-3xl font-bold leading-tight tracking-tight text-ink">
          Somebody is uploading a record tonight that nobody has played yet.
        </h2>
        <p className="text-pretty text-[15px] leading-relaxed text-soft">
          {signedIn
            ? 'Applications are open. Tell us what you would play and we will read it.'
            : `Make an account, then apply to host. ${brand.name} is people choosing records for each other, and that starts with somebody deciding to.`}
        </p>
      </div>

      <div className="flex flex-wrap gap-3">
        <LinkButton
          to={signedIn ? '/host' : '/register'}
          className="px-7 py-3.5 text-base font-bold"
          data-for-hosts-cta
        >
          {signedIn ? 'Apply to host' : 'Create an account'}
        </LinkButton>
        <Link
          to="/discover"
          className="inline-flex items-center px-2 py-3.5 text-sm text-accent hover:underline"
        >
          Hear what is on →
        </Link>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */

function Section({
  eyebrow,
  title,
  lead,
  children,
}: {
  eyebrow: string;
  title: string;
  lead?: string;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-7" data-hosts-section={eyebrow}>
      <div className="flex max-w-2xl flex-col gap-3">
        <span className="meta-caps text-faint">{eyebrow}</span>
        <h2 className="text-pretty text-2xl font-bold leading-[1.1] tracking-tight text-ink sm:text-3xl">
          {title}
        </h2>
        {lead !== undefined && (
          <p className="text-pretty text-[15px] leading-relaxed text-soft">{lead}</p>
        )}
      </div>

      {children}
    </section>
  );
}

/**
 * The grid every section uses.
 *
 * Two columns rather than three: these are sentences rather than labels, and at
 * three across a card is four words wide on the one screen size most people
 * read a marketing page on.
 */
function Cards({ children }: { children: ReactNode }) {
  return <div className="grid gap-4 sm:grid-cols-2">{children}</div>;
}

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2 rounded-[var(--radius-card)] border border-line bg-surface p-5">
      <h3 className="text-[15px] font-bold tracking-tight text-ink">{title}</h3>
      <p className="text-pretty text-[14px] leading-relaxed text-soft">{children}</p>
    </div>
  );
}
